
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Grid, Card, CardContent, Dialog, DialogTitle, 
  DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, 
  List, ListItem, ListItemText, ListItemAvatar, Avatar, IconButton, TextField, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import API_URL from '../config';
import { MapContainer, TileLayer, Marker, useMapEvents, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      if (setPosition) {
          setPosition(e.latlng);
          map.flyTo(e.latlng, map.getZoom());
      }
    },
  });

  return position ? <Marker position={position} /> : null;
}

function Punching() {
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [newLocation, setNewLocation] = useState({ lat: 25.0, lng: 45.0 }); // Default
  const [address, setAddress] = useState('');

  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [locRes, userRes] = await Promise.all([
        axios.get(`${API_URL}/punching/locations`, { headers }),
        axios.get(`${API_URL}/punching/users`, { headers })
      ]);
      setLocations(locRes.data);
      setUsers(userRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!selectedUser) return alert('Select a user');
    setLoading(true);
    try {
        await axios.post(`${API_URL}/punching/locations`, {
            user_id: selectedUser.id,
            latitude: newLocation.lat,
            longitude: newLocation.lng,
            address: address
        }, { headers });
        setOpen(false);
        fetchData();
        setSelectedUser(null);
        setAddress('');
    } catch (err) {
        alert('Error saving location');
    } finally {
        setLoading(false);
    }
  };

  const handleDelete = async (id) => {
      if(!window.confirm('Delete this location assignment?')) return;
      try {
          await axios.delete(`${API_URL}/punching/locations/${id}`, { headers });
          fetchData();
      } catch (err) {
          alert('Error deleting');
      }
  };

  const handleUserClick = (userId) => {
      navigate(`/punching/${userId}`);
  };

  const filteredLocations = locations.filter(loc => 
      (loc.user_name && loc.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (loc.address && loc.address.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, mb: 3, gap: 2 }}>
        <Typography variant="h5" fontWeight="bold">Assigned Locations</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField 
                placeholder="Search User or Location..." 
                size="small" 
                value={searchTerm} 
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                    sx: { borderRadius: 5, bgcolor: 'white' }
                }}
            />
            <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={() => setOpen(true)}
                sx={{ borderRadius: 5, whiteSpace: 'nowrap' }}
            >
                Add Assignment
            </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0', overflowX: 'auto' }}>
            <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Coordinates</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredLocations.map((loc) => (
                        <TableRow 
                            key={loc.id} 
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                            onClick={() => handleUserClick(loc.user_id)}
                        >
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                                        {loc.user_name ? loc.user_name.charAt(0) : '?'}
                                    </Avatar>
                                    <Typography variant="body2" fontWeight="medium">{loc.user_name}</Typography>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOnIcon fontSize="small" color="action" />
                                    <Typography variant="body2">{loc.address || 'No Address'}</Typography>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                    {loc.latitude.toFixed(6)}, {loc.longitude.toFixed(6)}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Button 
                                    size="small" 
                                    onClick={(e) => { e.stopPropagation(); handleUserClick(loc.user_id); }}
                                    sx={{ mr: 1 }}
                                >
                                    Details
                                </Button>
                                <IconButton 
                                    size="small" 
                                    color="error" 
                                    onClick={(e) => { e.stopPropagation(); handleDelete(loc.id); }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                    {filteredLocations.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                {locations.length === 0 ? "No users assigned to locations yet." : "No matches found."}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
      </TableContainer>

      {/* Add Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Assign Work Location</DialogTitle>
        <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                    <Box sx={{ mb: 2 }}>
                        <Autocomplete
                            options={users}
                            getOptionLabel={(option) => option.name || ''}
                            value={selectedUser}
                            onChange={(event, newValue) => setSelectedUser(newValue)}
                            renderInput={(params) => <TextField {...params} label="Search User" fullWidth />}
                        />
                    </Box>
                    <TextField 
                        fullWidth 
                        label="Location Name (e.g. Site Office)" 
                        value={address} 
                        onChange={e => setAddress(e.target.value)}
                        placeholder="e.g. Main Office, Site A"
                    />
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f9f9f9', borderRadius: 2 }}>
                        <Typography variant="caption" color="textSecondary" display="block">Selected Coordinates:</Typography>
                        <Typography variant="body2" fontWeight="bold">{newLocation.lat.toFixed(6)}, {newLocation.lng.toFixed(6)}</Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                            Click on the map to change the pin location.
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Box sx={{ height: 400, borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}>
                        <MapContainer center={[25, 45]} zoom={4} style={{ height: '100%', width: '100%' }}>
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <LocationMarker position={newLocation} setPosition={setNewLocation} />
                        </MapContainer>
                    </Box>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save Assignment'}
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Punching;
